import React, { useState, useEffect } from 'react';
import { FileText, Package, Loader, Filter, Download } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import api from '../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const Listino = ({ hideChrome = false }) => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filtri
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedFormat, setSelectedFormat] = useState('all');
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableFormats, setAvailableFormats] = useState([]);
  const [formats, setFormats] = useState([]);
  const [includeVAT, setIncludeVAT] = useState(false); // IVA esclusa di default
  const [discountTiers, setDiscountTiers] = useState([]);

  // Fasce di quantità per il listino - FALLBACK se API non risponde
  const quantityRangesFallback = [
    { label: '24-48 bot.', min_quantity: 24, max_quantity: 48, discount_percentage: 0.10 },
    { label: '49-96 bot.', min_quantity: 49, max_quantity: 96, discount_percentage: 0.15 },
    { label: '97-120 bot.', min_quantity: 97, max_quantity: 120, discount_percentage: 0.20 },
    { label: '121-420 bot.', min_quantity: 121, max_quantity: 420, discount_percentage: 0.25 },
    { label: '421-840 bot.', min_quantity: 421, max_quantity: 840, discount_percentage: 0.30 }
  ];

  // Usa le fasce dal DB se disponibili, altrimenti fallback
  const quantityRanges = discountTiers.length > 0 
    ? discountTiers.map(tier => ({
        label: tier.label,
        min: tier.min_quantity,
        max: tier.max_quantity,
        discount_percentage: tier.discount_percentage
      }))
    : quantityRangesFallback.map(tier => ({
        label: tier.label,
        min: tier.min_quantity,
        max: tier.max_quantity,
        discount_percentage: tier.discount_percentage
      }));

  useEffect(() => {
    fetchCollections();
    fetchFormats();
    fetchDiscountTiers();
  }, []);

  const fetchCollections = async () => {
    try {
      setLoading(true);
      
      // Recupera TUTTI i prodotti da /products/liquor-types (endpoint pubblico)
      const productsResponse = await api.get('/products/liquor-types');
      
      if (productsResponse.data && Array.isArray(productsResponse.data)) {
        // Filtra solo prodotti disponibili
        const availableProducts = productsResponse.data;
        
        // Estrai nomi prodotti unici per i filtri
        const productNames = [...new Set(availableProducts.map(p => p.name))].sort();
        setAvailableCategories(productNames);
        
        // Salva i prodotti per uso successivo
        setCollections([{ products: availableProducts }]);
      }
    } catch (err) {
      console.error('Errore nel recupero dei prodotti:', err);
      setError('Impossibile caricare il listino prezzi');
    } finally {
      setLoading(false);
    }
  };

  const fetchFormats = async () => {
    try {
      const response = await api.get('/formats');
      if (response.data && Array.isArray(response.data)) {
        setFormats(response.data);
        // Estrai i size_cl unici per i filtri
        const uniqueSizes = [...new Set(response.data.map(f => f.size_cl))].sort((a, b) => a - b);
        setAvailableFormats(uniqueSizes);
      }
    } catch (err) {
      console.error('Errore nel recupero dei formati:', err);
    }
  };

  const fetchDiscountTiers = async () => {
    try {
      const response = await api.get('/discount-tiers');
      if (response.data && Array.isArray(response.data)) {
        setDiscountTiers(response.data);
      }
    } catch (err) {
      console.error('Errore nel recupero delle fasce di sconto:', err);
      // Continua con i valori fallback hardcoded
    }
  };

  // Converti ml in cl
  const mlToCl = (ml) => {
    return ml / 10;
  };

  // Filtra i prodotti in base ai criteri selezionati
  const getFilteredProducts = (products) => {
    return products.filter(product => {
      // Prodotti da liquor-types non hanno is_active, hanno tutti disponibilità
      const productNameMatch = selectedCategory === 'all' || product.name === selectedCategory;
      
      // Per i prodotti liquor-types, usiamo il formato selezionato direttamente
      // Non filtriamo per volume perché i prodotti non hanno un volume fisso
      const formatMatch = selectedFormat === 'all' || true;
      
      return productNameMatch && formatMatch;
    });
  };

  // Ottieni il minimo ordine per un formato specifico
  const getMinOrderQuantity = (formatSizeCl) => {
    const format = formats.find(f => f.size_cl === formatSizeCl);
    return format ? format.min_order_quantity : 24;
  };

  // Calcola il prezzo per una specifica quantità con formato e sconti
  const calculatePrice = (basePrice, quantity, formatSizeCl) => {
    // Trova il formato selezionato per ottenere il price_multiplier
    const format = formats.find(f => f.size_cl === formatSizeCl);
    if (!format) return basePrice.toFixed(2);
    
    // Applica il moltiplicatore del formato
    const priceWithFormat = basePrice * format.price_multiplier;
    
    // Trova la fascia di sconto appropriata dalla configurazione dinamica
    let discount = 0;
    const applicableTier = quantityRanges.find(
      tier => quantity >= tier.min && quantity <= tier.max
    );
    
    if (applicableTier) {
      discount = applicableTier.discount_percentage;
    }
    
    let finalPrice = priceWithFormat * (1 - discount);
    
    // Applica IVA se richiesto (22%)
    if (includeVAT) {
      finalPrice = finalPrice * 1.22;
    }
    
    return finalPrice.toFixed(2);
  };

  // Genera e scarica PDF del listino filtrato
  const downloadPDF = () => {
    const doc = new jsPDF();
    
    // Header con dati aziendali
    doc.setFontSize(20);
    doc.setTextColor(126, 34, 206); // Purple color
    doc.text('Listino Prezzi', 14, 20);
    
    // Dati aziendali
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.text('Lacinio SRL - IlTuoAmaro.it', 14, 28);
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('P.IVA: 03714460791 | REA: KR - 184420 | Licenza Doganale: IT00KRX00033F', 14, 33);
    doc.text('Via G. Mercalli, 16 Z.I. - 88900 Crotone (KR)', 14, 37);
    doc.text('Tel: +39 393 9587340 | Email: info@iltuoamaro.it', 14, 41);
    
    // Linea separatrice
    doc.setDrawColor(200, 200, 200);
    doc.line(14, 45, 196, 45);
    
    // Info filtri
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    let yPos = 52;
    
    if (selectedCategory !== 'all') {
      doc.text(`Prodotto: ${selectedCategory}`, 14, yPos);
      yPos += 6;
    }
    if (selectedFormat !== 'all') {
      doc.text(`Formato: ${selectedFormat}cl`, 14, yPos);
      yPos += 6;
    }
    doc.text(`Prezzi: ${includeVAT ? 'IVA inclusa (22%)' : 'IVA esclusa'}`, 14, yPos);
    yPos += 10;
    
    // Prepara i dati per la tabella
    if (selectedCategory !== 'all' && selectedFormat !== 'all') {
      const filteredProducts = getFilteredProducts(collections[0]?.products || []);
      
      if (filteredProducts.length > 0) {
        // Headers della tabella
        const headers = [
          'Prodotto',
          'Formato',
          'Gradazione',
          'Min. Ordine',
          ...quantityRanges.map(range => range.label)
        ];
        
        // Righe della tabella
        const rows = filteredProducts.map(product => [
          product.name,
          `${parseFloat(selectedFormat)}cl`,
          product.alcoholContent || 'N/A',
          `${getMinOrderQuantity(parseFloat(selectedFormat))} bot.`,
          ...quantityRanges.map(range => 
            `€${calculatePrice(product.basePrice || 0, range.min, parseFloat(selectedFormat))}`
          )
        ]);
        
        // Genera la tabella
        autoTable(doc, {
          startY: yPos,
          head: [headers],
          body: rows,
          theme: 'grid',
          headStyles: { 
            fillColor: [126, 34, 206],
            fontSize: 8,
            fontStyle: 'bold'
          },
          bodyStyles: {
            fontSize: 8
          },
          columnStyles: {
            0: { cellWidth: 35 },
            1: { cellWidth: 20 },
            2: { cellWidth: 20 },
            3: { cellWidth: 20 }
          },
          margin: { top: 10 }
        });
        
        // Note importanti
        const finalY = doc.lastAutoTable.finalY + 10;
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text('Note Importanti:', 14, finalY);
        
        doc.setFontSize(8);
        doc.setTextColor(80, 80, 80);
        const notes = [
          `• I prezzi sono mostrati ${includeVAT ? 'con IVA inclusa (22%)' : 'al netto dell\'IVA'}`,
          '• I prezzi indicati sono per bottiglia nelle rispettive fasce di quantità',
          '• Per ricette personalizzate, il minimo ordine è di 120 bottiglie',
          '• Spedizione gratuita a partire da 120 bottiglie formato 50cl o superiore',
          '• Per ordini superiori a 840 bottiglie, richiedi un preventivo personalizzato',
          '• I prezzi possono variare in base alla tipologia di bottiglia ed etichetta'
        ];
        
        let noteY = finalY + 6;
        notes.forEach(note => {
          doc.text(note, 14, noteY);
          noteY += 5;
        });
        
        // Footer con dati aziendali
        const pageHeight = doc.internal.pageSize.height;
        doc.setFontSize(7);
        doc.setTextColor(120, 120, 120);
        doc.text(`Documento generato il ${new Date().toLocaleDateString('it-IT')}`, 14, pageHeight - 15);
        doc.text('Lacinio SRL - P.IVA: 03714460791 - www.iltuoamaro.it', 14, pageHeight - 10);
        doc.text('Via G. Mercalli, 16 Z.I. - 88900 Crotone (KR) - Tel: +39 393 9587340', 14, pageHeight - 6);
        
      } else {
        doc.setFontSize(12);
        doc.text('Nessun prodotto trovato con i filtri selezionati.', 14, yPos);
      }
    } else {
      doc.setFontSize(12);
      doc.text('Seleziona un prodotto e un formato per generare il listino.', 14, yPos);
    }
    
    // Salva il PDF
    const filename = `listino_${selectedCategory !== 'all' ? selectedCategory : 'tutti'}_${selectedFormat !== 'all' ? selectedFormat + 'cl' : 'formati'}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
  };

  if (loading) {
    return (
      <div className={hideChrome ? '' : 'min-h-screen bg-gray-50'}>
        {!hideChrome && <Header />}
        <div className="flex items-center justify-center py-20">
          <Loader className="w-8 h-8 animate-spin text-purple-600" />
          <span className="ml-3 text-gray-600">Caricamento listino...</span>
        </div>
        {!hideChrome && <Footer />}
      </div>
    );
  }

  if (error) {
    return (
      <div className={hideChrome ? '' : 'min-h-screen bg-gray-50'}>
        {!hideChrome && <Header />}
        <div className="max-w-4xl mx-auto px-4 py-20">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-700">{error}</p>
          </div>
        </div>
        {!hideChrome && <Footer />}
      </div>
    );
  }

  return (
    <div className={hideChrome ? '' : 'min-h-screen bg-gray-50'}>
      {!hideChrome && <Header />}
      
      <div className={hideChrome ? 'py-2' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'}>
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <FileText className="w-10 h-10 text-purple-600 mr-3" />
            <h1 className="text-4xl font-bold text-gray-900">Listino Prezzi</h1>
          </div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Prezzi per fasce di quantità - Tutti i nostri prodotti disponibili
          </p>
        </div>

        {/* Filtri */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Filter className="w-5 h-5 text-purple-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Filtra Prodotti</h3>
            </div>
            {selectedCategory !== 'all' && selectedFormat !== 'all' && (
              <button
                onClick={downloadPDF}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Download className="w-4 h-4 mr-2" />
                Scarica PDF
              </button>
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {/* Filtro Nome Prodotto */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome Prodotto
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white cursor-pointer appearance-none"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.5rem center',
                  backgroundSize: '1.5em 1.5em',
                  paddingRight: '2.5rem'
                }}
              >
                <option value="all">Tutti i Prodotti</option>
                {availableCategories.map(productName => (
                  <option key={productName} value={productName}>
                    {productName}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtro Formato */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Formato Bottiglia
              </label>
              <select
                value={selectedFormat}
                onChange={(e) => setSelectedFormat(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white cursor-pointer appearance-none"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.5rem center',
                  backgroundSize: '1.5em 1.5em',
                  paddingRight: '2.5rem'
                }}
              >
                <option value="all">Tutti i Formati</option>
                {availableFormats.map(sizeCl => (
                  <option key={sizeCl} value={sizeCl}>
                    {sizeCl}cl
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Opzione IVA */}
          <div className="md:col-span-2 mt-4 pt-4 border-t border-gray-200">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={includeVAT}
                onChange={(e) => setIncludeVAT(e.target.checked)}
                className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer"
              />
              <span className="text-sm font-medium text-gray-700">
                Mostra prezzi con IVA inclusa (22%)
              </span>
            </label>
          </div>

          {/* Reset Filtri */}
          {(selectedCategory !== 'all' || selectedFormat !== 'all') && (
            <div className="md:col-span-2 mt-2">
              <button
                onClick={() => {
                  setSelectedCategory('all');
                  setSelectedFormat('all');
                }}
                className="text-sm text-purple-600 hover:text-purple-700 font-medium"
              >
                Resetta tutti i filtri
              </button>
            </div>
          )}
        </div>

        {/* Messaggio: seleziona prodotto e formato */}
        {(selectedCategory === 'all' || selectedFormat === 'all') && (
          <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-8 text-center">
            <Package className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-blue-900 mb-2">
              Seleziona Prodotto e Formato
            </h3>
            <p className="text-blue-700">
              Per visualizzare il listino prezzi, seleziona un prodotto specifico e il formato desiderato dai filtri qui sopra.
            </p>
          </div>
        )}

        {/* Collezioni e Prodotti - Mostra solo se entrambi i filtri sono selezionati */}
        {selectedCategory !== 'all' && selectedFormat !== 'all' && (
          <div className="space-y-8">
            {collections.map((collection) => {
              const filteredProducts = getFilteredProducts(collection.products);
              
              // Non mostrare la collezione se non ci sono prodotti dopo il filtro
              if (filteredProducts.length === 0) return null;
            
            return (
              <div key={collection.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
                {/* Intestazione Collezione */}
                <div className="bg-gradient-to-r from-purple-600 to-purple-800 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Package className="w-6 h-6 text-white mr-3" />
                      <div>
                        <h2 className="text-2xl font-bold text-white">{collection.title}</h2>
                        {collection.subtitle && (
                          <p className="text-purple-100 text-sm">{collection.subtitle}</p>
                        )}
                      </div>
                    </div>
                    <span className="text-purple-100 text-sm">
                      {filteredProducts.length} {filteredProducts.length === 1 ? 'prodotto' : 'prodotti'}
                    </span>
                  </div>
                </div>

                {/* Tabella Prodotti */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Nome Prodotto
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Formato
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Gradazione
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Minimo Ordine
                        </th>
                        {quantityRanges.map((range, idx) => (
                          <th key={idx} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <div>{range.label}</div>
                            <div className="text-xs font-normal text-gray-400 mt-1">
                              {includeVAT ? 'IVA incl.' : 'IVA escl.'}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredProducts
                        .sort((a, b) => a.sortOrder - b.sortOrder)
                        .map((product, idx) => (
                          <tr key={product.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                {/* Icona bottiglia invece dell'immagine */}
                                <div className="w-10 h-10 rounded-md bg-purple-100 flex items-center justify-center mr-3">
                                  <Package className="w-6 h-6 text-purple-600" />
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {product.name}
                                  </div>
                                  {product.category && (
                                    <div className="text-xs text-gray-500">
                                      {product.category}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="text-sm text-gray-900">{parseFloat(selectedFormat)}cl</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="text-sm text-gray-900">{product.alcoholContent || 'N/A'}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="text-sm text-gray-900">{getMinOrderQuantity(parseFloat(selectedFormat))} bot.</span>
                            </td>
                            {quantityRanges.map((range, idx) => (
                              <td key={idx} className="px-6 py-4 whitespace-nowrap text-center">
                                <span className="text-sm font-semibold text-gray-900">
                                  €{calculatePrice(product.basePrice || 0, range.min, parseFloat(selectedFormat))}
                                </span>
                              </td>
                            ))}
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
            
            {/* Messaggio se non ci sono prodotti */}
            {collections.every(c => getFilteredProducts(c.products).length === 0) && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
                <p className="text-yellow-800 text-lg">
                  Nessun prodotto trovato con i filtri selezionati.
                </p>
                <button
                  onClick={() => {
                    setSelectedCategory('all');
                    setSelectedFormat('all');
                  }}
                  className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Resetta Filtri
                </button>
              </div>
            )}
          </div>
        )}

        {/* Note */}
        <div className="mt-8 bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">Note Importanti</h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li>• I prezzi sono mostrati {includeVAT ? 'con IVA inclusa (22%)' : 'al netto dell\'IVA'}</li>
            <li>• I prezzi indicati sono per bottiglia nelle rispettive fasce di quantità</li>
            <li>• Il listino mostra prezzi fino a 840 bottiglie</li>
            <li>• <strong>Per ricette personalizzate, il minimo ordine è di 120 bottiglie</strong></li>
            <li>• <strong>Per ordini superiori a 840 bottiglie, richiedi un preventivo personalizzato</strong></li>
            <li>• Spedizione gratuita per ordini superiori a 120 bottiglie</li>
            <li>• I prezzi variano in base al formato selezionato e alle quantità ordinate</li>
            <li>• <strong>I prezzi possono subire variazioni in base alla tipologia di bottiglia, dell'etichetta o in base ad aumenti dei costi delle materie prime</strong></li>
          </ul>
        </div>

        {/* CTA Registrati */}
        <div className="mt-8 bg-gradient-to-r from-green-500 to-green-700 rounded-lg p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-3">Registrati Subito</h3>
          <p className="text-green-100 mb-6 max-w-2xl mx-auto">
            Crea il tuo account B2B e accedi al nostro configuratore per iniziare a ordinare i tuoi liquori personalizzati
          </p>
          <a
            href="/register"
            className="inline-flex items-center px-8 py-4 bg-white text-green-700 font-bold rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
          >
            <Package className="w-5 h-5 mr-2" />
            Registrati Ora
          </a>
        </div>

        {/* CTA Preventivo Personalizzato */}
        <div className="mt-8 bg-gradient-to-r from-purple-600 to-purple-800 rounded-lg p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-3">Hai bisogno di più di 840 bottiglie?</h3>
          <p className="text-purple-100 mb-6 max-w-2xl mx-auto">
            Per ordini superiori a 840 bottiglie o per esigenze particolari, 
            crea il tuo preventivo personalizzato utilizzando il nostro configuratore
          </p>
          <a
            href="/builder"
            className="inline-flex items-center px-8 py-4 bg-white text-purple-700 font-bold rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
          >
            <Package className="w-5 h-5 mr-2" />
            Richiedi Preventivo Personalizzato
          </a>
        </div>
      </div>

      {!hideChrome && <Footer />}
    </div>
  );
};

export default Listino;
